/**
 * Global "Sync Buckets to Broker" flow.
 *
 * Mounted once at App root. Listens for the ``tm:open-rebalance`` window
 * event and drives the full preview → confirm modal without navigating
 * the user away from the current page. This lets the Portfolios page's
 * amber "buckets out of sync" banner sync in-place rather than kicking
 * the user over to Settings.
 *
 * Behaviour is a lift-and-shift of the original Settings.jsx logic:
 *   - POST /broker/portfolios/rebalance/preview  → open modal
 *   - Force-override sub-flow (type "FORCE SWITCH" + reason)
 *   - Intent-resolution sub-modal for blocking records
 *   - POST /broker/portfolios/rebalance/confirm  → close + notify
 *
 * After a successful confirm we fire ``tm:broker-changed`` so pages
 * that show broker state (Portfolios, BrokerCockpit) refresh.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { notifyBrokerChanged } from "@/lib/executionSource";

export default function RebalanceBucketsFlow() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [force, setForce] = useState(false);
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");

  // Intent Resolution nested modal
  const [resolveIntent, setResolveIntent] = useState(null);
  const [resolveChoice, setResolveChoice] = useState("abandoned");
  const [resolveReason, setResolveReason] = useState("");
  const [resolveSubmitting, setResolveSubmitting] = useState(false);

  const openPreview = useCallback(async () => {
    if (loading || preview) return;   // already open / loading
    setLoading(true);
    setForce(false); setReason(""); setTyped("");
    try {
      const { data } = await api.post("/broker/portfolios/rebalance/preview");
      setPreview(data);
      // Preview endpoint clears a stale needs_rebalance_review flag when
      // buckets are already aligned — nudge any listeners to refresh.
      notifyBrokerChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load rebalance preview");
    } finally { setLoading(false); }
  }, [loading, preview]);

  useEffect(() => {
    const h = () => openPreview();
    window.addEventListener("tm:open-rebalance", h);
    return () => window.removeEventListener("tm:open-rebalance", h);
  }, [openPreview]);

  const confirmRebalance = async () => {
    if (!preview) return;
    const br = preview.broker_snapshot || {};
    setConfirming(true);
    try {
      const body = {
        idempotency_key: `${preview.preview_token}-${Date.now()}`,
        expected_equity: br.equity,
        expected_cash: br.cash,
        expected_position_value: br.invested_value,
        expected_execution_source: preview.execution_source?.after,
        force,
      };
      if (force) {
        body.reason = reason.trim();
        body.typed_confirmation = typed.trim();
      }
      const { data } = await api.post("/broker/portfolios/rebalance/confirm", body);
      if (data.no_op) {
        toast.success("No rebalance needed — buckets already match this broker account");
      } else if (data.idempotent_replay) {
        toast.success("Rebalance already applied (idempotent replay)");
      } else {
        toast.success(`Reset ${data.buckets_reset} bucket${data.buckets_reset === 1 ? "" : "s"} · Baseline: ${(data.snapshot_id || "").slice(0, 12)}…`);
      }
      setPreview(null);
      notifyBrokerChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.startsWith("REBALANCE_STALE_PREVIEW")) {
        toast.error("Broker state changed since preview — refreshing…");
        openPreview();
      } else if (detail.startsWith("REBALANCE_HARD_BLOCK")) {
        toast.error("Hard block — cannot be overridden. Resolve at the broker first.");
      } else if (detail.startsWith("REBALANCE_FORCE_CONFIRMATION")) {
        toast.error("Type FORCE SWITCH exactly to confirm.");
      } else if (detail.startsWith("REBALANCE_FORCE_REASON")) {
        toast.error("Provide a reason of at least 10 characters.");
      } else if (detail.startsWith("REBALANCE_FORCE_ROLE")) {
        toast.error("Only workspace owners can force override.");
      } else if (detail.startsWith("REBALANCE_BLOCKED")) {
        toast.error("Blocked by open records — enable Force and provide a reason.");
      } else {
        toast.error(detail || "Rebalance failed");
      }
    } finally { setConfirming(false); }
  };

  const openResolve = (intent) => {
    setResolveIntent(intent);
    setResolveChoice("abandoned");
    setResolveReason("");
  };

  const submitResolveIntent = async () => {
    if (!resolveIntent) return;
    const trimmed = resolveReason.trim();
    if (trimmed.length < 10) {
      toast.error("Reason must be at least 10 characters");
      return;
    }
    setResolveSubmitting(true);
    try {
      await api.post(`/broker/orders/intents/${resolveIntent.id}/resolve`, {
        resolution: resolveChoice, reason: trimmed,
      });
      toast.success(`Intent ${resolveIntent.id.slice(-6)} resolved — refreshing preview`);
      setResolveIntent(null); setResolveReason("");
      openPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resolve failed");
    } finally { setResolveSubmitting(false); }
  };

  if (!preview) return null;

  const fmt2 = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

  return (
    <>
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
           data-testid="rebalance-preview-modal"
           onClick={(e) => { if (e.target === e.currentTarget) setPreview(null); }}>
        <div className="bg-background border border-border/80 max-w-3xl w-full max-h-[85vh] overflow-y-auto">
          <div className="p-4 border-b border-border/60 flex items-center justify-between">
            <div>
              <div className="label-cap">Sync Buckets to Broker</div>
              <div className="text-sm font-medium mt-1">Review the changes before confirming</div>
            </div>
            <button className="btn-outline" onClick={() => setPreview(null)}
                    data-testid="rebalance-preview-close">Cancel</button>
          </div>
          <div className="p-4 space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-border/60 p-3">
                <div className="label-cap mb-2">Broker Snapshot</div>
                <div className="flex justify-between"><span className="text-muted-foreground">Mode</span><span className="mono">{preview.broker_snapshot?.mode}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Equity</span><span className="mono">${fmt2(preview.broker_snapshot?.equity)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Cash</span><span className="mono">${fmt2(preview.broker_snapshot?.cash)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Invested</span><span className="mono">${fmt2(preview.broker_snapshot?.invested_value)}</span></div>
                <div className="flex justify-between font-medium"><span>Deployable</span><span className="mono pnl-up">${fmt2(preview.broker_snapshot?.deployable_cash)}</span></div>
              </div>
              <div className="border border-border/60 p-3">
                <div className="label-cap mb-2">Execution Source</div>
                <div className="flex justify-between"><span className="text-muted-foreground">Before</span><span className="chip text-signal-yellow border-current">{preview.execution_source?.before}</span></div>
                <div className="flex justify-between mt-2"><span className="text-muted-foreground">After</span><span className="chip pnl-up border-current">{preview.execution_source?.after}</span></div>
                <div className="flex justify-between mt-2 text-xs"><span className="text-muted-foreground">Fingerprint</span><span className={`mono ${preview.fingerprint?.changed ? "pnl-up" : "text-muted-foreground"}`}>{preview.fingerprint?.changed ? "CHANGED" : "UNCHANGED"}</span></div>
                <div className="mt-2 text-[11px] text-muted-foreground">Mock realized P&amp;L and closed trades will be archived to <span className="mono">portfolio_baselines</span> and remain accessible.</div>
              </div>
            </div>
            <div className="border border-border/60">
              <div className="grid grid-cols-5 gap-2 p-2 text-[11px] label-cap border-b border-border/60">
                <div>Bucket</div>
                <div className="text-right">Before Cash</div>
                <div className="text-right">After Cash</div>
                <div className="text-right">Before P&amp;L</div>
                <div className="text-right">After P&amp;L</div>
              </div>
              {(preview.buckets || []).map((b) => (
                <div key={b.id} className="grid grid-cols-5 gap-2 p-2 text-sm border-b border-border/40 last:border-b-0"
                     data-testid={`rebalance-row-${b.key}`}>
                  <div>{b.name} <span className="text-[10px] text-muted-foreground">({b.allocation_pct}%)</span></div>
                  <div className="text-right mono">${fmt2(b.before?.available_cash)}</div>
                  <div className="text-right mono pnl-up">${fmt2(b.after?.available_cash)}</div>
                  <div className="text-right mono">${fmt2(b.before?.realized_pnl)}</div>
                  <div className="text-right mono text-muted-foreground">$0.00</div>
                </div>
              ))}
            </div>
            {(preview.hard_blocks || []).length > 0 && (
              <div className="border pnl-down border-current p-3">
                <div className="label-cap mb-1">HARD BLOCKS · CANNOT BE OVERRIDDEN</div>
                {preview.hard_blocks.map((b, i) => (
                  <div key={i} className="text-[11px] mt-1" data-testid={`rebalance-hardblock-${b.code}`}>
                    <span className="mono mr-2">{b.code}</span>{b.detail}
                  </div>
                ))}
              </div>
            )}
            {(preview.warnings || []).length > 0 && (
              <div className="space-y-1">
                {preview.warnings.map((w, i) => (
                  <div key={i} className={`text-[11px] p-2 border ${w.severity === "HIGH" ? "pnl-down border-current" : w.severity === "MEDIUM" ? "text-signal-yellow border-current" : "text-muted-foreground border-border/60"}`}
                       data-testid={`rebalance-warning-${w.code}`}>
                    <span className="label-cap mr-2">{w.severity}</span>{w.detail}
                  </div>
                ))}
              </div>
            )}
            {preview.blocking_records?.open_order_intents?.length > 0 && (
              <div className="border border-border/60">
                <div className="p-2 label-cap border-b border-border/60">Pending Order Intents</div>
                {preview.blocking_records.open_order_intents.map((oi) => (
                  <div key={oi.id} className="grid grid-cols-6 gap-2 p-2 text-[11px] border-b border-border/40 last:border-b-0"
                       data-testid={`rebalance-intent-${oi.id.slice(-8)}`}>
                    <div className="mono col-span-1">{oi.symbol} <span className="text-muted-foreground">{oi.side}</span></div>
                    <div className="mono">{oi.order_type} · {oi.requested_qty}</div>
                    <div><span className="chip text-signal-yellow border-current">{oi.status}</span></div>
                    <div className="text-muted-foreground truncate">{oi.broker_order_id || "no broker id"}</div>
                    <div className="text-muted-foreground text-right">{oi.execution_source}</div>
                    <div className="text-right"><button className="btn-outline !text-[10px] !px-2 !py-0.5" onClick={() => openResolve(oi)} data-testid={`rebalance-intent-resolve-${oi.id.slice(-8)}`}>Resolve</button></div>
                  </div>
                ))}
              </div>
            )}
            {preview.blocking_records?.open_broker_positions?.length > 0 && (
              <div className="border border-border/60">
                <div className="p-2 label-cap border-b border-border/60">Broker Positions</div>
                {preview.blocking_records.open_broker_positions.map((bp) => (
                  <div key={bp.id} className="grid grid-cols-5 gap-2 p-2 text-[11px] border-b border-border/40 last:border-b-0">
                    <div className="mono">{bp.symbol}</div>
                    <div className="mono text-right">{bp.confirmed_qty} @ ${(bp.avg_entry_price ?? 0).toFixed(2)}</div>
                    <div className="mono text-right">{bp.execution_source || "—"}</div>
                    <div className="text-right"><span className={`chip ${bp.ownership === "MAPPED" ? "pnl-up" : "text-signal-yellow"} border-current`}>{bp.ownership}</span></div>
                    <div className="text-muted-foreground text-right text-[10px]">{bp.recommended_action}</div>
                  </div>
                ))}
              </div>
            )}
            {preview.blocks_switch && !preview.hard_blocks?.length && preview.force_allowed && (
              <div className="border pnl-down border-current p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)}
                         data-testid="rebalance-force-checkbox"/>
                  <span className="font-medium">Force override — resolve pending records manually later</span>
                </label>
                {force && (
                  <div className="space-y-2 pt-1 border-t border-current/30">
                    <div>
                      <div className="label-cap mb-1">Reason (min 10 chars, stored on audit)</div>
                      <textarea className="input w-full text-xs" rows={2}
                                value={reason} onChange={(e) => setReason(e.target.value)}
                                placeholder="e.g. Two abandoned mock test intents from a prior session — will not affect Alpaca"
                                data-testid="rebalance-force-reason"/>
                    </div>
                    <div>
                      <div className="label-cap mb-1">Type <span className="mono">FORCE SWITCH</span> to confirm</div>
                      <input className="input w-full text-xs mono" value={typed}
                             onChange={(e) => setTyped(e.target.value)}
                             placeholder="FORCE SWITCH" data-testid="rebalance-force-typed"/>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="p-4 border-t border-border/60 flex justify-end gap-2">
            <button className="btn-outline" onClick={() => setPreview(null)} data-testid="rebalance-cancel-btn">Cancel</button>
            <button className="btn-danger" onClick={confirmRebalance}
                    disabled={confirming
                              || (preview.hard_blocks?.length > 0)
                              || (preview.blocks_switch && !force)
                              || (force && (reason.trim().length < 10
                                             || typed.trim().toUpperCase() !== "FORCE SWITCH"))}
                    data-testid="rebalance-confirm-btn">
              {confirming ? "Applying…"
                : preview.hard_blocks?.length > 0 ? "Blocked"
                : force ? "Force and Switch"
                : "Confirm and Switch"}
            </button>
          </div>
        </div>
      </div>

      {/* Intent Resolution — nested */}
      {resolveIntent && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
             data-testid="resolve-intent-modal"
             onClick={(e) => { if (e.target === e.currentTarget && !resolveSubmitting) setResolveIntent(null); }}>
          <div className="bg-background border border-border/80 max-w-lg w-full">
            <div className="p-4 border-b border-border/60">
              <div className="label-cap">Resolve Order Intent</div>
              <div className="text-sm font-medium mt-1">
                {resolveIntent.symbol} · {resolveIntent.side} · {resolveIntent.order_type} · qty {resolveIntent.requested_qty}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1 mono">
                {resolveIntent.broker_order_id || "no broker id"} · {resolveIntent.execution_source}
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <div className="label-cap mb-2">Resolution</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[
                    { v: "cancelled", label: "Cancelled", hint: "Intent was cancelled at broker or never sent" },
                    { v: "abandoned", label: "Abandoned", hint: "Legacy / no longer relevant — will not be re-submitted" },
                    { v: "duplicate_legacy", label: "Duplicate Legacy", hint: "Duplicates a MOCK-era intent that never reached Alpaca" },
                    { v: "manual_review", label: "Manual Review", hint: "Escalate for investigation — do not close" },
                  ].map(opt => (
                    <label key={opt.v}
                           className={`border p-2 cursor-pointer ${resolveChoice === opt.v ? "border-current pnl-up" : "border-border/60"}`}>
                      <div className="flex items-center gap-2">
                        <input type="radio" name="resolve-choice"
                               checked={resolveChoice === opt.v}
                               onChange={() => setResolveChoice(opt.v)}
                               data-testid={`resolve-choice-${opt.v}`}/>
                        <span className="font-medium">{opt.label}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1">{opt.hint}</div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="label-cap mb-1">Reason (min 10 chars — stored on audit)</div>
                <textarea className="input w-full text-xs" rows={3}
                          value={resolveReason}
                          onChange={(e) => setResolveReason(e.target.value)}
                          placeholder="e.g. Legacy MOCK stop_limit from a prior fork — never reached Alpaca"
                          data-testid="resolve-reason-input"/>
                <div className="text-[10px] text-muted-foreground mt-1">{resolveReason.trim().length}/10 minimum</div>
              </div>
            </div>
            <div className="p-4 border-t border-border/60 flex justify-end gap-2">
              <button className="btn-outline" onClick={() => setResolveIntent(null)}
                      disabled={resolveSubmitting} data-testid="resolve-cancel-btn">Back</button>
              <button className="btn-danger" onClick={submitResolveIntent}
                      disabled={resolveSubmitting || resolveReason.trim().length < 10}
                      data-testid="resolve-submit-btn">
                {resolveSubmitting ? "Resolving…" : "Resolve Intent"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
